import { ref } from 'vue'
import type { AxiosError } from 'axios'
import { isAxiosError as axiosIsAxiosError } from 'axios'
import { TOAST_DURATION_MS } from '../constants/apiEndpoints'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { logger } from '@/utils/eventLogger'

export interface JournalHttpErrorContext {
  function: string
  store?: string
  payload?: unknown
}

/** Отправляет 400/500 ошибки журналов на бэкенд. */
export function logJournalHttpError(error: unknown, context: JournalHttpErrorContext) {
  if (!axiosIsAxiosError(error)) {
    return
  }

  const status = error.response?.status
  if (status !== 400 && status !== 500) {
    return
  }

  logger.error(`${context.function}_error`, {
    store: context.store ?? 'journals',
    function: context.function,
    status,
    error: `❌ ${error}`,
    payload: context.payload,
  })
}

export interface ApiError {
  message: string
  code?: string
  details?: Record<string, string[]>
}

export function useErrorHandler() {
  const lastError = ref<ApiError | null>(null)
  const feedbackStore = useFeedbackStore()

  /** Парсит ответ/ошибку axios в ApiError. */
  function parseError(error: unknown): ApiError {
    if (!error) {
      return { message: 'Неизвестная ошибка' }
    }

    if (isAxiosError(error)) {
      const data = error.response?.data

      if (data && typeof data === 'object') {
        return {
          message: data.message || data.detail || 'Ошибка сервера',
          code: data.code,
          details: data.errors || data.details,
        }
      }

      return {
        message: error.message || 'Ошибка при выполнении запроса',
        code: String(error.response?.status),
      }
    }

    if (error instanceof Error) {
      return {
        message: error.message,
      }
    }

    return {
      message: String(error),
    }
  }

  /** Обрабатывает ошибку, логирует 400/500 на бэкенд и показывает toast. */
  function handleError(error: unknown, customMessage?: string, logContext?: JournalHttpErrorContext) {
    const apiError = parseError(error)

    lastError.value = apiError
    feedbackStore.showToast({
      type: 'error',
      message: customMessage || apiError.message,
      time: TOAST_DURATION_MS,
    })
    console.error('API Error', apiError)

    if (error instanceof Error && import.meta.env.DEV) {
      console.error('Error stack', error.stack)
    }

    logJournalHttpError(error, logContext ?? {
      function: 'journalApi',
      store: 'journals',
      payload: { message: customMessage || apiError.message },
    })

    return apiError
  }

  function showSuccess(message: string) {
    feedbackStore.showToast({
      type: 'success',
      message,
      time: TOAST_DURATION_MS,
    })
  }

  function showInfo(message: string) {
    feedbackStore.showToast({
      type: 'info',
      message,
      time: TOAST_DURATION_MS,
    })
  }

  function showWarning(message: string) {
    feedbackStore.showToast({
      type: 'warning',
      message,
      time: TOAST_DURATION_MS,
    })
  }

  function isAxiosError(error: unknown): error is AxiosError {
    return error?.isAxiosError === true
  }

  return {
    lastError,
    handleError,
    parseError,
    showSuccess,
    showInfo,
    showWarning,
  }
}
